import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ConfirmationService, MessageService, TreeNode } from 'primeng/api';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { InputSwitchModule } from 'primeng/inputswitch';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TabViewModule } from 'primeng/tabview';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { TreeTableModule } from 'primeng/treetable';
import { v4 as uuidv4 } from 'uuid';
import { CrudTable } from '../../../model/CrudTable';
import { HttpResponse } from '../../../model/HttpResponse';
import { Modalidad } from '../../../model/Modalidad';
import { Category } from '../../../model/competitions/Category';
import { CategoryLevel } from '../../../model/competitions/Level';
import { CategoryLevelService } from '../../../services/category-level.service';
import { CategoryService } from '../../../services/category.service';
import { LoadingService } from '../../../services/loading.service';
import { PageHeaderComponent } from "../../shared/page-header/page-header.component";
import { CrudTableComponent } from '../../shared/tables/crud-table/crud-table.component';

@Component({
    selector: 'app-niveles-categorias',
    standalone: true,
    templateUrl: './niveles-categorias.component.html',
    styleUrl: './niveles-categorias.component.scss',
    providers: [MessageService, ConfirmationService],
    imports: [
        AvatarModule,
        ButtonModule,
        InputTextareaModule, 
        SelectButtonModule, 
        InputSwitchModule, 
        InputTextModule, 
        DropdownModule,
        TabViewModule,
        TooltipModule,
        TreeTableModule,
        PageHeaderComponent, 
        CrudTableComponent, 
        ConfirmDialogModule, 
        CommonModule, 
        DialogModule, 
        ReactiveFormsModule,
        ToastModule
    ]
})
export class NivelesCategoriaComponent implements OnInit {
    
    modalitiesTabs = [
        {
            label: 'Artístico',
            value: Modalidad.ARTISTICO,
        },
        {
            label: 'Carreras',
            value: Modalidad.CARRERAS,
        },
        {
            label: 'Hockey',
            value: Modalidad.HOCKEY,
        },
        {
            label: 'Skateboarding',
            value: Modalidad.SKATEBOARDING,
        }
    ];
    modalitieTabIndex: number = 0;
    modalitieSelected: Modalidad = Modalidad.ARTISTICO;
    
    treeData: TreeNode[] = [];
    cols: any[] = [];
    
    levelsCrudTableData: CrudTable = {
        data: [],
        dataKey: '',
        headers: [],
        items: [],
    };

    categoriesCrudTableData: CrudTable = {
        data: [],
        dataKey: '',
        headers: [],
        items: [],
    };

    validateLevelForm!: FormGroup;
    validateCategoryForm!: FormGroup;
    showLevelForm: boolean = false;
    showCategoryForm: boolean = false;
    isNewLevelForm: boolean = false;
    isUpdateLevelForm: boolean = false;
    isNewCategoryForm: boolean = false;
    isUpdateCategoryForm: boolean = false;
    levelDialogHeader?: string;
    categoryDialogHeader?: string;
    activeTab: string = 'levels';

    stateOptions: any[] = [
        { label: 'No', value: false },
        { label: 'Si', value: true },
    ];

    modalityOptions: any[] = [
        { label: 'ARTISTICO', value: Modalidad.ARTISTICO },
        { label: 'CARRERAS', value: Modalidad.CARRERAS },
        { label: 'HOCKEY', value: Modalidad.HOCKEY },
        { label: 'SKATEBOARDING', value: Modalidad.SKATEBOARDING },
    ];

    categoryStatusOptions: any[] = [
        { label: 'Inactivo', value: 0 },
        { label: 'Activo', value: 1 },
    ];

    availableLevels: CategoryLevel[] = [];

    constructor(
        private fb: FormBuilder,
        private loadingService: LoadingService,
        private categoryLevelService: CategoryLevelService,
        private categoryService: CategoryService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService,
    ) {}

    ngOnInit(): void {
        this.initializeForms();
        this.initializeColumns();
        this.onModalitieTabChange({ index: this.modalitieTabIndex });
    }

    initializeForms(): void {
        this.validateLevelForm = this.fb.group({
            name: [null, [Validators.required]],
            modality: [null, [Validators.required]],
            isActive: [true, [Validators.required]]
        });

        this.validateCategoryForm = this.fb.group({
            name: [null, [Validators.required]],
            idCategoryLevel: [null, [Validators.required]],
            modality: [null, [Validators.required]],
            minYearsOld: [null, [Validators.required, Validators.min(0)]],
            maxYearsOld: [null, [Validators.required, Validators.min(0)]],
            description: [null, [Validators.required]],
            isActive: [true, [Validators.required]]
        });
    }

    initializeColumns(): void {
        this.cols = [
            { field: 'name', header: 'Nombre', width: '25%' },
            { field: 'type', header: 'Tipo', width: '12%' },
            { field: 'modality', header: 'Modalidad', width: '15%' },
            { field: 'ageRange', header: 'Rango Edad', width: '15%' },
            { field: 'status', header: 'Estado', width: '13%' },
            { field: 'actions', header: 'Acciones', width: '20%' }
        ];
    }

    setActiveTab(tab: string) {
        this.activeTab = tab;
    }

    onModalitieTabChange(event: any) {
        this.modalitieSelected = this.modalitiesTabs[event.index].value;
        this.loadTreeData();
    }

    async loadTreeData() {
        this.loadingService.showLoading();
        try {
            // Cargar niveles
            const levelsResponse = await this.categoryLevelService.getAllCategoryLevels(this.modalitieSelected).toPromise();
            const levels = levelsResponse?.response || [];
            
            // Cargar categorías
            const categoriesResponse = await this.categoryService.getAllCategories(this.modalitieSelected).toPromise();
            const categories = categoriesResponse?.response || [];
            
            // Construir datos del árbol
            this.buildTreeData(levels, categories);
            this.buildLevelsCrudTable(levels);
            this.buildCategoriesCrudTable(categories);
            this.availableLevels = levels;
        } catch (error) {
        }
        this.loadingService.hideLoading();
    }

    buildTreeData(levels: CategoryLevel[], categories: Category[]): void {
        this.treeData = levels.map(level => {
            const levelCategories = categories.filter(cat => cat.idCategoryLevel === level.id);
            
            const levelNode: TreeNode = {
                data: {
                    id: level.id,
                    name: level.name,
                    type: 'Nivel',
                    modality: level.modality,
                    status: level.isActive ? 'Activo' : 'Inactivo',
                    ageRange: '-',
                    entityType: 'level',
                    entity: level
                },
                children: levelCategories.map(category => ({
                    data: {
                        id: category.id,
                        name: category.name,
                        type: 'Categoría',
                        modality: category.modality,
                        status: category.isActive ? 'Activo' : 'Inactivo',
                        ageRange: `${category.minYearsOld} - ${category.maxYearsOld}`,
                        entityType: 'category',
                        entity: category
                    }
                }))
            };

            return levelNode;
        });
    }

    // TREETABLE CRUD METHODS
    addNodeItem(parentNode?: any) {
        if (!parentNode) {
            this.addLevel();
        } else if (parentNode.node?.data?.entityType === 'level') {
            this.addCategoryToLevel(parentNode.node.data.entity);
        }
    }

    editNodeItem(rowNode: any) {
        if (rowNode.node?.data?.entityType === 'level') {
            this.editLevel(rowNode.node.data.entity);
        } else if (rowNode.node?.data?.entityType === 'category') {
            this.editCategory(rowNode.node.data.entity);
        }
    }

    deleteNodeItem(rowNode: any) {
        if (rowNode.node?.data?.entityType === 'level') {
            this.deleteLevel(rowNode.node.data.entity);
        } else if (rowNode.node?.data?.entityType === 'category') {
            this.deleteCategory(rowNode.node.data.entity);
        }
    }

    addCategoryToLevel(level: CategoryLevel) {
        // Pre-seleccionar el nivel en el formulario de categoría
        this.getCategoryFormNew();
        this.validateCategoryForm.patchValue({
            idCategoryLevel: level.id
        });
    }

    // CATEGORY LEVELS METHODS
    async selectCategoryLevels() {
        this.loadingService.showLoading();
        this.categoryLevelService.getAllCategoryLevels(this.modalitieSelected)
            .subscribe((res: HttpResponse) => {
                this.buildLevelsCrudTable(res.response);
                this.availableLevels = res.response;
                this.loadingService.hideLoading();
            });
    }

    buildLevelsCrudTable(data: any[]) {
        this.levelsCrudTableData = {
            data: data,
            dataKey: 'id',
            headers: [
                {
                    label: 'Nombre',
                    sorted: true,
                    sortItem: 'name',
                },
                {
                    label: 'Modalidad',
                    sorted: true,
                    sortItem: 'modality',
                },
                {
                    label: 'Activo',
                    sorted: true,
                    sortItem: 'isActive',
                },
                {
                    label: 'Subniveles',
                    sorted: false,
                    sortItem: null,
                },
                {
                    label: 'Acciones',
                    sorted: false,
                    sortItem: null,
                },
            ],
            items: ['name', 'modality', 'isActive', 'sublevels'],
        };
    }

    addLevel() {
        this.getLevelFormNew();
    }

    editLevel(item: CategoryLevel) {
        this.getLevelFormRenewal(item);
    }

    deleteLevel(item: CategoryLevel) {
        this.confirmationService.confirm({
            message: 'Estas seguro de borrar este registro ?',
            header: 'Confirmar',
            acceptLabel: 'Si',
            rejectLabel: 'No',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.loadingService.showLoading();
                this.categoryLevelService.deleteCategoryLevel(item)
                    .subscribe((res: HttpResponse) => {
                        if (res.code === 200) {
                            this.messageService.add({
                                severity: 'success',
                                summary: 'OK',
                                detail: 'El registro se ha eliminado correctamente!',
                                life: 3000,
                            });
                        } else {
                            this.messageService.add({
                                severity: 'error',
                                summary: 'Error',
                                detail: 'Ocurrió un error eliminando el registro, inténtelo nuevamente si el error persiste contacte el administrador del sistema',
                                life: 3000,
                            });
                        }
                        this.loadTreeData();
                        this.loadingService.hideLoading();
                    });
            },
        });
    }

    getLevelFormNew() {
        this.validateLevelForm = this.fb.group({
            name: [null, [Validators.required]],
            modality: [this.modalitieSelected, [Validators.required]],
            isActive: [true, [Validators.required]]
        });
        this.levelDialogHeader = 'Nuevo Nivel de Categoría';
        this.isNewLevelForm = true;
        this.isUpdateLevelForm = false;
        this.showLevelForm = true;
    }

    getLevelFormRenewal(item: CategoryLevel) {
        this.validateLevelForm = this.fb.group({
            id: [item.id, [Validators.required]],
            name: [item.name, [Validators.required]],
            modality: [item.modality, [Validators.required]],
            isActive: [item.isActive, [Validators.required]],
            sublevels: [item.sublevels || []],
            customData: [item.customData]
        });
        this.levelDialogHeader = `Editar ${item.name}`;
        this.isNewLevelForm = false;
        this.isUpdateLevelForm = true;
        this.showLevelForm = true;
    }

    closeLevelDialog() {
        this.showLevelForm = false;
    }

    addNewLevel() {
        if (this.validateLevelForm.valid) {
            this.loadingService.showLoading();
            const item = this.buildCategoryLevel(this.validateLevelForm.value);
            this.categoryLevelService.createCategoryLevel(item)
                .subscribe((res: HttpResponse) => {
                    if (res.code === 200) {
                        this.messageService.add({
                            severity: 'success',
                            summary: 'OK',
                            detail: 'El registro se ha creado correctamente!',
                            life: 3000,
                        });
                    } else {
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Ocurrió un error creando el registro, inténtelo nuevamente si el error persiste contacte el administrador del sistema',
                            life: 3000,
                        });
                    }
                    this.loadTreeData();
                    this.showLevelForm = false;
                    this.loadingService.hideLoading();
                });
        } else {
            this.messageService.add({
                severity: 'warn',
                summary: 'Verificar',
                detail: 'Verifique que todos los campos obligatorios del formulario estén completos',
                life: 3000,
            });
        }
    }

    editExistentLevel() {
        this.confirmationService.confirm({
            message: '¿Estas seguro de actualizar este registro?',
            header: 'Confirmar',
            acceptLabel: 'Si',
            rejectLabel: 'No',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.loadingService.showLoading();
                const item = this.buildCategoryLevel(this.validateLevelForm.value);
                this.categoryLevelService.updateCategoryLevel(item)
                    .subscribe((res: HttpResponse) => {
                        if (res.code === 200) {
                            this.messageService.add({
                                severity: 'success',
                                summary: 'OK',
                                detail: 'El registro se ha actualizado correctamente!',
                                life: 3000,
                            });
                        } else {
                            this.messageService.add({
                                severity: 'error',
                                summary: 'Error',
                                detail: 'Ocurrió un error actualizando el registro, inténtelo nuevamente si el error persiste contacte el administrador del sistema',
                                life: 3000,
                            });
                        }
                        this.loadTreeData();
                        this.showLevelForm = false;
                        this.loadingService.hideLoading();
                    });
            },
        });
    }

    buildCategoryLevel(form: any): CategoryLevel {
        let item: CategoryLevel = {
            id: form['id'] || uuidv4(),
            name: form['name'].toUpperCase(),
            modality: form['modality'],
            isActive: form['isActive'],
            customData: form['customData'],
            sublevels: form['sublevels'] || []
        };

        return item;
    }

    // CATEGORIES METHODS
    async selectCategories() {
        this.loadingService.showLoading();
        this.categoryService.getAllCategories(this.modalitieSelected)
            .subscribe((res: HttpResponse) => {
                this.buildCategoriesCrudTable(res.response);
                this.loadingService.hideLoading();
            });
    }

    buildCategoriesCrudTable(data: any[]) {
        this.categoriesCrudTableData = {
            data: data,
            dataKey: 'id',
            headers: [
                {
                    label: 'Nombre',
                    sorted: true,
                    sortItem: 'name',
                },
                {
                    label: 'Nivel',
                    sorted: true,
                    sortItem: 'idCategoryLevel',
                },
                {
                    label: 'Modalidad',
                    sorted: true,
                    sortItem: 'modality',
                },
                {
                    label: 'Edad Mín',
                    sorted: true,
                    sortItem: 'minYearsOld',
                },
                {
                    label: 'Edad Máx',
                    sorted: true,
                    sortItem: 'maxYearsOld',
                },
                {
                    label: 'Estado',
                    sorted: true,
                    sortItem: 'status',
                },
                {
                    label: 'Acciones',
                    sorted: false,
                    sortItem: null,
                },
            ],
            items: ['name', 'idCategoryLevel', 'modality', 'minYearsOld', 'maxYearsOld', 'status'],
        };
    }

    addCategory() {
        this.getCategoryFormNew();
    }

    editCategory(item: Category) {
        this.getCategoryFormRenewal(item);
    }

    deleteCategory(item: Category) {
        this.confirmationService.confirm({
            message: 'Estas seguro de borrar este registro ?',
            header: 'Confirmar',
            acceptLabel: 'Si',
            rejectLabel: 'No',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.loadingService.showLoading();
                this.categoryService.deleteCategory(item)
                    .subscribe((res: HttpResponse) => {
                        if (res.code === 200) {
                            this.messageService.add({
                                severity: 'success',
                                summary: 'OK',
                                detail: 'El registro se ha eliminado correctamente!',
                                life: 3000,
                            });
                        } else {
                            this.messageService.add({
                                severity: 'error',
                                summary: 'Error',
                                detail: 'Ocurrió un error eliminando el registro, inténtelo nuevamente si el error persiste contacte el administrador del sistema',
                                life: 3000,
                            });
                        }
                        this.loadTreeData();
                        this.loadingService.hideLoading();
                    });
            },
        });
    }

    getCategoryFormNew() {
        this.filterAvailableLevelsByModality();
        
        this.validateCategoryForm = this.fb.group({
            name: [null, [Validators.required]],
            idCategoryLevel: [null, [Validators.required]],
            modality: [this.modalitieSelected, [Validators.required]],
            minYearsOld: [null, [Validators.required, Validators.min(0)]],
            maxYearsOld: [null, [Validators.required, Validators.min(0)]],
            description: [null, [Validators.required]],
            isActive: [true, [Validators.required]]
        });
        this.categoryDialogHeader = 'Nueva Categoría';
        this.isNewCategoryForm = true;
        this.isUpdateCategoryForm = false;
        this.showCategoryForm = true;
    }

    getCategoryFormRenewal(item: Category) {
        // Filtrar niveles disponibles por modalidad
        this.filterAvailableLevelsByModality();
        
        this.validateCategoryForm = this.fb.group({
            id: [item.id, [Validators.required]],
            name: [item.name, [Validators.required]],
            idCategoryLevel: [item.idCategoryLevel, [Validators.required]],
            modality: [item.modality, [Validators.required]],
            minYearsOld: [item.minYearsOld, [Validators.required, Validators.min(0)]],
            maxYearsOld: [item.maxYearsOld, [Validators.required, Validators.min(0)]],
            description: [item.description, [Validators.required]],
            isActive: [item.isActive, [Validators.required]],
            customData: [item.customData],
            subcategories: [item.subcategories || []]
        });
        this.categoryDialogHeader = `Editar ${item.name}`;
        this.isNewCategoryForm = false;
        this.isUpdateCategoryForm = true;
        this.showCategoryForm = true;
    }

    filterAvailableLevelsByModality() {
    }

    closeCategoryDialog() {
        this.showCategoryForm = false;
    }

    addNewCategory() {
        if (this.validateCategoryForm.valid && this.validateAgeRange()) {
            this.loadingService.showLoading();
            const item = this.buildCategory(this.validateCategoryForm.value);
            this.categoryService.createCategory(item)
                .subscribe((res: HttpResponse) => {
                    if (res.code === 200) {
                        this.messageService.add({
                            severity: 'success',
                            summary: 'OK',
                            detail: 'La categoría se ha creado correctamente!',
                            life: 3000,
                        });
                    } else {
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Ocurrió un error creando la categoría, inténtelo nuevamente si el error persiste contacte el administrador del sistema',
                            life: 3000,
                        });
                    }
                    this.loadTreeData();
                    this.showCategoryForm = false;
                    this.loadingService.hideLoading();
                });
        } else if (!this.validateCategoryForm.valid) {
            this.markFormGroupTouched(this.validateCategoryForm);
            this.messageService.add({
                severity: 'warn',
                summary: 'Verificar',
                detail: 'Verifique que todos los campos obligatorios del formulario estén completos',
                life: 3000,
            });
        }
    }

    editExistentCategory() {
        if (this.validateCategoryForm.valid && this.validateAgeRange()) {
            this.confirmationService.confirm({
                message: '¿Estas seguro de actualizar este registro?',
                header: 'Confirmar',
                acceptLabel: 'Si',
                rejectLabel: 'No',
                icon: 'pi pi-exclamation-triangle',
                accept: () => {
                    this.loadingService.showLoading();
                    const item = this.buildCategory(this.validateCategoryForm.value);
                    this.categoryService.updateCategory(item)
                        .subscribe((res: HttpResponse) => {
                            if (res.code === 200) {
                                this.messageService.add({
                                    severity: 'success',
                                    summary: 'OK',
                                    detail: 'La categoría se ha actualizado correctamente!',
                                    life: 3000,
                                });
                            } else {
                                this.messageService.add({
                                    severity: 'error',
                                    summary: 'Error',
                                    detail: 'Ocurrió un error actualizando el registro, inténtelo nuevamente si el error persiste contacte el administrador del sistema',
                                    life: 3000,
                                });
                            }
                            this.loadTreeData();
                            this.showCategoryForm = false;
                            this.loadingService.hideLoading();
                        });
                },
            });
        } else if (!this.validateCategoryForm.valid) {
            this.markFormGroupTouched(this.validateCategoryForm);
            this.messageService.add({
                severity: 'warn',
                summary: 'Verificar',
                detail: 'Verifique que todos los campos obligatorios del formulario estén completos',
                life: 3000,
            });
        }
    }

    buildCategory(form: any): Category {
        let item: Category = {
            id: form['id'] || uuidv4(),
            name: form['name'].toUpperCase(),
            idCategoryLevel: form['idCategoryLevel'],
            modality: form['modality'],
            minYearsOld: form['minYearsOld'],
            maxYearsOld: form['maxYearsOld'],
            description: form['description'],
            isActive: form['isActive'],
            customData: form['customData'],
            subcategories: form['subcategories'] || []
        };

        return item;
    }

    getLevelName(levelId: string): string {
        const level = this.availableLevels.find(l => l.id === levelId);
        return level ? level.name : levelId;
    }

    validateAgeRange(): boolean {
        const minAge = this.validateCategoryForm.get('minYearsOld')?.value;
        const maxAge = this.validateCategoryForm.get('maxYearsOld')?.value;

        if (minAge !== null && maxAge !== null && maxAge < minAge) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Error de validación',
                detail: 'La edad máxima debe ser mayor o igual que la edad mínima',
                life: 3000,
            });
            return false;
        }
        return true;
    }

    markFormGroupTouched(formGroup: FormGroup) {
        Object.keys(formGroup.controls).forEach(key => {
            const control = formGroup.get(key);
            control?.markAsTouched();
            
            if (control instanceof FormGroup) {
                this.markFormGroupTouched(control);
            }
        });
    }

}